/**
 * Authentication service for AETHER.
 * Handles Cognito OAuth2 flow with Amazon Federate (SAML).
 */
import { AUTH_CONFIG } from './config';

/** Decoded JWT payload from Cognito ID token. */
export interface AuthUser {
  email: string;
  sub: string;
  exp: number;
  /** Cognito/LDAP groups carried in the `cognito:groups` claim. */
  groups: string[];
}

/** Token set from Cognito token endpoint. */
interface TokenResponse {
  id_token: string;
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
}

const TOKEN_STORAGE_KEY = 'aether_id_token';
const ACCESS_TOKEN_KEY = 'aether_access_token';
const REFRESH_TOKEN_KEY = 'aether_refresh_token';
const USER_KEY = 'aether_user';

/** Refresh the ID token when fewer than this many seconds remain before expiry. */
const REFRESH_THRESHOLD_SECONDS = 300;

/**
 * Redirects the browser to Cognito Hosted UI for login via Amazon Federate.
 */
export function redirectToLogin(): void {
  const loginUrl =
    `https://${AUTH_CONFIG.domain}/oauth2/authorize` +
    `?response_type=code` +
    `&client_id=${AUTH_CONFIG.clientId}` +
    `&redirect_uri=${encodeURIComponent(AUTH_CONFIG.redirectUri)}` +
    `&identity_provider=${AUTH_CONFIG.identityProvider}` +
    `&scope=${AUTH_CONFIG.scopes}`;
  window.location.href = loginUrl;
}

/**
 * Exchanges an authorization code for tokens via the Cognito token endpoint.
 * Called from the /callback route after Federate redirects back.
 */
export async function exchangeCodeForTokens(code: string): Promise<AuthUser> {
  const tokenUrl = `https://${AUTH_CONFIG.domain}/oauth2/token`;
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: AUTH_CONFIG.clientId,
    redirect_uri: AUTH_CONFIG.redirectUri,
    code,
  });

  try {
    const response = await fetch(tokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Token exchange failed: ${response.status} ${errorText}`);
    }

    const tokens: TokenResponse = await response.json();
    storeTokens(tokens);
    return decodeIdToken(tokens.id_token);
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    throw new Error(`Authentication failed: ${message}`);
  }
}

/**
 * Logs out the user: clears local tokens and redirects to Cognito logout.
 */
export function logout(): void {
  sessionStorage.removeItem(TOKEN_STORAGE_KEY);
  sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  sessionStorage.removeItem(USER_KEY);

  const logoutUrl =
    `https://${AUTH_CONFIG.domain}/logout` +
    `?client_id=${AUTH_CONFIG.clientId}` +
    `&logout_uri=${encodeURIComponent(AUTH_CONFIG.logoutUri)}`;
  window.location.href = logoutUrl;
}

/**
 * Returns the current authenticated user from storage, or null if not logged in.
 * Does NOT clear tokens on ID-token expiry — a valid refresh token can still
 * renew the session. Use `ensureFreshToken` to obtain a usable ID token.
 */
export function getCurrentUser(): AuthUser | null {
  const stored = sessionStorage.getItem(USER_KEY);
  if (!stored) return null;
  try {
    return JSON.parse(stored) as AuthUser;
  } catch {
    return null;
  }
}

/**
 * Returns the stored ID token for API authorization headers.
 */
export function getIdToken(): string | null {
  return sessionStorage.getItem(TOKEN_STORAGE_KEY);
}

/**
 * Derives the user alias (characters before '@') from an email address.
 * Returns '' for an empty/invalid value.
 */
export function aliasFromEmail(email: string | undefined | null): string {
  if (!email) return '';
  const at = email.indexOf('@');
  return at > 0 ? email.slice(0, at) : email;
}

/**
 * Returns the current user's alias (email local-part), or '' if not logged in.
 */
export function getCurrentUserAlias(): string {
  const user = getCurrentUser();
  return aliasFromEmail(user?.email);
}

/**
 * Returns true when the user is allowed to create/edit records.
 *
 * Access to this application is gated upstream by Amazon Federate, whose
 * "Allowed Groups" is set to the `aether-gamma` LDAP group. Federate only issues
 * a SAML assertion to members of that group, so **any user who successfully
 * authenticates is, by definition, an `aether-gamma` member** and therefore has
 * edit access. The Federate IdP maps only `email` into the token (not LDAP
 * groups), so we must NOT gate on the `cognito:groups` claim — that claim only
 * carries the internal `..._AmazonFederate` group and would wrongly lock out
 * real users.
 *
 * If `cognito:groups` is ever enriched to carry the `aether-gamma` LDAP group
 * (e.g. via a SAML group attribute mapping or a pre-token-generation trigger),
 * this can be tightened to require AUTH_CONFIG.editGroup explicitly.
 */
export function userHasEditAccess(user: AuthUser | null): boolean {
  if (!user) return false;

  // If the token explicitly carries the edit group, honor it (future-proofing
  // for when Federate maps the LDAP group into cognito:groups).
  const editGroup = AUTH_CONFIG.editGroup.toLowerCase();
  if (user.groups.some((g) => g.toLowerCase() === editGroup)) return true;

  // Otherwise, rely on Federate's upstream Allowed-Groups gate: a valid,
  // authenticated session means the user is an aether-gamma LDAP member.
  return true;
}

/**
 * True when the user may manage the note abbreviation list (Add/Edit/Delete).
 *
 * Unlike {@link userHasEditAccess}, this is gated strictly on membership in the
 * abbreviations-admin group (MXO-L6) as carried in the `cognito:groups` claim —
 * that group is now released into the token, so the claim is authoritative.
 * This only controls UI visibility; the API enforces the same group server-side.
 */
export function userHasAbbreviationAdmin(user: AuthUser | null): boolean {
  if (!user) return false;
  const group = AUTH_CONFIG.abbreviationsAdminGroup.toLowerCase();
  return user.groups.some((g) => g.toLowerCase() === group);
}

/**
 * Decodes the payload from a JWT token (base64url encoded).
 */
function decodeIdToken(idToken: string): AuthUser {
  const payload = idToken.split('.')[1];
  const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  const rawGroups = decoded['cognito:groups'];
  return {
    email: decoded.email || '',
    sub: decoded.sub || '',
    exp: decoded.exp || 0,
    groups: Array.isArray(rawGroups) ? (rawGroups as string[]) : [],
  };
}

/**
 * Stores tokens in sessionStorage (cleared on tab close). Cognito omits the
 * refresh token on refresh responses, so an existing one is preserved when the
 * new response doesn't include it.
 */
function storeTokens(tokens: TokenResponse): void {
  sessionStorage.setItem(TOKEN_STORAGE_KEY, tokens.id_token);
  sessionStorage.setItem(ACCESS_TOKEN_KEY, tokens.access_token);
  if (tokens.refresh_token) {
    sessionStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
  }
  const user = decodeIdToken(tokens.id_token);
  sessionStorage.setItem(USER_KEY, JSON.stringify(user));
}

/** Clears all session tokens without redirecting (used when refresh fails). */
export function clearSession(): void {
  sessionStorage.removeItem(TOKEN_STORAGE_KEY);
  sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  sessionStorage.removeItem(USER_KEY);
}

/** Seconds until the stored ID token expires (negative if already expired). */
export function secondsUntilExpiry(): number {
  const user = getCurrentUser();
  if (!user) return -1;
  return Math.floor((user.exp * 1000 - Date.now()) / 1000);
}

// Single-flight guard so concurrent callers share one refresh network request.
let refreshInFlight: Promise<boolean> | null = null;

/**
 * Exchanges the stored refresh token for a new ID/access token via Cognito.
 * Returns true on success. On failure (e.g. refresh token revoked/expired)
 * clears the session and returns false. Concurrent calls share one request.
 */
export function refreshTokens(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  const refreshToken = sessionStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) return Promise.resolve(false);

  refreshInFlight = (async (): Promise<boolean> => {
    try {
      const tokenUrl = `https://${AUTH_CONFIG.domain}/oauth2/token`;
      const body = new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: AUTH_CONFIG.clientId,
        refresh_token: refreshToken,
      });
      const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (!response.ok) {
        clearSession();
        return false;
      }
      const tokens: TokenResponse = await response.json();
      storeTokens(tokens);
      return true;
    } catch {
      // Network error: keep the session (caller can retry) but report failure.
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/**
 * Ensures a usable ID token: refreshes when the current token is missing or
 * within the refresh threshold of expiry. Returns the valid token, or null if
 * no valid session can be obtained.
 */
export async function ensureFreshToken(): Promise<string | null> {
  const remaining = secondsUntilExpiry();
  if (remaining > REFRESH_THRESHOLD_SECONDS) {
    return getIdToken();
  }
  try {
    const refreshed = await refreshTokens();
    return refreshed ? getIdToken() : null;
  } catch {
    return null;
  }
}
